import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { combineLatest, from, map, Observable } from 'rxjs';
import { App } from '../models/app.model';
import { Clinic } from '../models/clinic.model';
import { Specialist } from '../models/specialist.model';
import { User } from '../models/user.model';
import { convertSnaps } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  constructor(private db: AngularFireDatabase) {}

  findApps(): Observable<App[]> {
    return this.db
      .list('apps')
      .snapshotChanges()
      .pipe(map((results) => convertSnaps<App>(results)));
  }

  countEntitiesByAppId(
    appId: string,
  ): Observable<{ users: number; clinics: number; specialists: number }> {
    const usersCount$ = this.db
      .list(`${appId}_users`)
      .snapshotChanges()
      .pipe(
        map((results) => convertSnaps<User>(results)),
        map((users) => users.filter((user) => user.clinicId)),
        map((filteredUsers) => filteredUsers.length),
      );

    const clinicsCount$ = this.db
      .list(`${appId}_clinics`)
      .snapshotChanges()
      .pipe(map((results) => convertSnaps<Clinic>(results).length));

    const specialistsCount$ = this.db
      .list(`${appId}_specialists`)
      .snapshotChanges()
      .pipe(map((results) => convertSnaps<Specialist>(results).length));

    return combineLatest([usersCount$, clinicsCount$, specialistsCount$]).pipe(
      map(([users, clinics, specialists]) => ({ users, clinics, specialists })),
    );
  }

  createApp(app: App, appId?: string): Observable<App> {
    let save$: Observable<any>;

    if (appId) {
      save$ = from(this.db.object(`apps/${appId}`).set(app));
    } else {
      save$ = from(this.db.list('apps').push(app));
    }

    return save$.pipe(
      map((res) => {
        const newAppId = appId ?? res.key;
        return {
          id: newAppId,
          ...app,
        };
      }),
    );
  }

  private createDynamicNodes(appId: string): void {
    const nodes = ['visits', 'users', 'clinics', 'specialists', 'pets'];
    nodes.forEach((node) => {
      this.db
        .object(`${appId}_${node}`)
        .set({})
        .catch((error) => {
          console.error(`Error al crear el nodo ${node}:`, error);
        });
    });
  }
}
